import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForcePasswordChange = () => {
  const { user, setUser } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  if (!user?.force_password_change) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/auth/change-password`, { new_password: newPassword }, { withCredentials: true });
      toast.success('Password updated successfully!');
      // If this was the forced-first-time flow, flip is_first_login=true so
      // the Welcome experience triggers via the router-level guard.
      setUser(prev => ({
        ...prev,
        force_password_change: false,
        is_first_login: res.data?.is_first_login ? true : prev?.is_first_login,
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="force-password-change-modal">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 sm:p-8">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-[#E63946]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-7 h-7 text-[#E63946]" />
          </div>
          <h2 className="text-xl font-bold text-[#002249]">Welcome to the Chamber Portal</h2>
          <p className="text-sm text-slate-500 mt-2">For your security, please set a new password before continuing.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">New Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="pl-10 h-11" placeholder="Enter new password" required data-testid="new-password-input" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Confirm Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="pl-10 h-11" placeholder="Confirm new password" required data-testid="confirm-password-input" />
            </div>
          </div>
          <Button type="submit" disabled={saving} className="w-full h-11 bg-[#E63946] hover:bg-[#d32f3f] text-white font-semibold" data-testid="change-password-submit">
            {saving ? 'Updating...' : 'Set New Password'}
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

export default ForcePasswordChange;
